import { relations, sql } from 'drizzle-orm'
import {
  boolean,
  check,
  date,
  decimal,
  integer,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getNullableIdReference,
} from '../common/utils'
import { attachments } from './attachment'
import { emailAccounts } from './emailAccount'
import { employeeCertificates } from './employeeCertificate'
import { employeeSalaries } from './employeeSalary'
import { employeeSkills } from './employeeSkill'
import { teams } from './team'
import { users } from './user'

export const genderEnum = pgEnum('gender', [
  'male',
  'female',
  'other',
  'prefer_not_to_say',
])

export const availabilityModeEnum = pgEnum('availability_mode', [
  'default',
  'explicit',
])

export const employees = pgTable(
  'employees',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id),
    role: text().notNull(), // Employee role within the organization
    employmentStartDate: date(), // First day of employment; floors the timesheet range
    // Last day of employment; null = still employed. A future date is a fixed-term
    // contract and takes effect on its own; see docs/superpowers/specs/2026-08-12-offboarding-design.md
    employmentEndDate: date(),
    birthDate: date(),
    gender: genderEnum(),
    avatarAttachmentId: getNullableIdReference(() => attachments.id),
    defaultLatitude: decimal({ precision: 10, scale: 8 }),
    defaultLongitude: decimal({ precision: 11, scale: 8 }),
    defaultLocationAddress: text(),
    availabilityMode: availabilityModeEnum().default('default').notNull(),
    bookableServicePresets: text('bookable_service_presets')
      .array()
      .default(sql`'{}'::text[]`)
      .notNull(),
    defaultHelperUserId: getNullableIdReference(() => users.id),
    commissionPercent: decimal({ precision: 5, scale: 2 }), // For subcontractors: percentage of visit revenue to pay
    // For subcontractors: the company invoicing us, so incoming invoices can be
    // matched to the person who did the work.
    subcontractorCompanyName: text(),
    // A subcontractor under the 20 000 € threshold invoices without VAT, so
    // the cash flow forecast must not add it to their commission.
    subcontractorVatLiable: boolean().default(true).notNull(),
    // Share of working time spent on field work; the rest is supervision,
    // dispatch and quoting. Scales the person's available days in the capacity
    // model, so a manager who sweeps one day a week counts as one day, not five.
    fieldWorkPercent: decimal({ precision: 5, scale: 2 })
      .default('100.00')
      .notNull(),
    teamId: getNullableIdReference(() => teams.id),
    // Sweeper-specific nuohous pace; NULL falls back to the preset's defaults
    // (25 min first fireplace, +15 min each further one). Shorter values let
    // an experienced sweeper fit more visits into a booking window.
    nuohousFirstFireplaceMinutes: integer(),
    nuohousExtraFireplaceMinutes: integer(),
    // Length of the repeating area rotation in weeks (employee_rotation_days)
    rotationWeeks: integer().notNull().default(1),
  },
  (table) => [
    ...getBaseIndexes('employees', table),
    check('employees_rotation_weeks_check', sql`${table.rotationWeeks} >= 1`),
  ],
)

// Employee relations
export const employeesRelations = relations(employees, ({ one, many }) => ({
  user: one(users, {
    fields: [employees.userId],
    references: [users.id],
  }),
  avatar: one(attachments, {
    fields: [employees.avatarAttachmentId],
    references: [attachments.id],
  }),
  defaultHelper: one(users, {
    fields: [employees.defaultHelperUserId],
    references: [users.id],
    relationName: 'defaultHelper',
  }),
  team: one(teams, {
    fields: [employees.teamId],
    references: [teams.id],
    relationName: 'teamMembers',
  }),
  employeeSkills: many(employeeSkills),
  employeeSalaries: many(employeeSalaries),
  employeeCertificates: many(employeeCertificates),
  emailAccounts: many(emailAccounts),
}))
